import { createContext, useContext, useState, useCallback, useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';

/**
 * PWA Context - Smart Install Prompt (Issue #209)
 *
 * Hybrid strategy: Show install prompt up to 3 times OR until user
 * explicitly dismisses — whichever comes first.
 *
 * localStorage keys:
 * - pwa-prompt-dismissed: boolean — user clicked X/dismiss
 * - pwa-prompt-show-count: number — times shown this device
 */

const DISMISSED_KEY = 'pwa-prompt-dismissed';
const SHOW_COUNT_KEY = 'pwa-prompt-show-count';
const MAX_SHOWS = 3;

function isEligibleToShow(): boolean {
  try {
    if (localStorage.getItem(DISMISSED_KEY) === 'true') return false;
    const count = parseInt(localStorage.getItem(SHOW_COUNT_KEY) || '0', 10);
    return count < MAX_SHOWS;
  } catch {
    return false; // localStorage blocked (incognito, etc.)
  }
}

function incrementShowCount(): void {
  try {
    const count = parseInt(localStorage.getItem(SHOW_COUNT_KEY) || '0', 10);
    localStorage.setItem(SHOW_COUNT_KEY, String(count + 1));
  } catch {
    // Ignore storage errors
  }
}

interface PWAContextValue {
  showInstallPrompt: () => void;
  hideInstallPrompt: () => void;
  dismissInstallPrompt: () => void;
  isPromptVisible: boolean;
  /**
   * True when at least one floating action button (FAB) is currently mounted
   * on screen (#3096). The install prompts suppress themselves while a FAB is
   * present so the z-50 prompt card never covers a core Action-layer control.
   */
  isFabPresent: boolean;
  /**
   * Register a mounted FAB. Returns an unregister cleanup. Prefer the
   * `useRegisterFab` hook, which wires this to a component's lifecycle.
   */
  registerFab: () => () => void;
}

const PWAContext = createContext<PWAContextValue | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components -- #3601 hook co-located with its provider
export function usePWA() {
  const context = useContext(PWAContext);
  if (!context) {
    throw new Error('usePWA must be used within a PWAProvider');
  }
  return context;
}

/**
 * Register the calling component as a mounted FAB while `active` is true (#3096).
 * While any FAB is registered, `isFabPresent` is true and the PWA install
 * prompts hide. Tolerant of being called outside a PWAProvider (no-op) so a
 * FAB-bearing component can be unit-tested in isolation.
 */
// eslint-disable-next-line react-refresh/only-export-components -- #3601 hook co-located with its provider
export function useRegisterFab(active: boolean): void {
  const context = useContext(PWAContext);
  const registerFab = context?.registerFab;

  // useLayoutEffect (not useEffect): registration coordinates paint, not data.
  // Navigating onto a FAB view while a prompt is already visible must suppress
  // the z-50 card BEFORE the browser paints, or it flashes over the FAB for one
  // frame (#3096). Layout effects run synchronously post-commit, pre-paint.
  useLayoutEffect(() => {
    if (!active || !registerFab) return;
    return registerFab();
  }, [active, registerFab]);
}

interface PWAProviderProps {
  children: ReactNode;
}

export function PWAProvider({ children }: PWAProviderProps) {
  const [isPromptVisible, setIsPromptVisible] = useState(false);
  const [fabCount, setFabCount] = useState(0);
  // Mirror of fabCount readable synchronously from stable callbacks (#3096).
  // showInstallPrompt is a useCallback with no deps, so it cannot close over the
  // latest fabCount state; it reads this ref instead. Kept in lock-step with the
  // setFabCount calls inside registerFab.
  const fabCountRef = useRef(0);
  // A show requested while a FAB was present (#3096). We defer rather than
  // discard: this bit is set at request time and replayed by the fabCount
  // effect below once the FAB count settles back to 0, so a prompt suppressed
  // at fire-time is never lost for the whole session. PWAManager fires
  // showInstallPrompt exactly once, above the routes — without this, landing on
  // a FAB view at fire-time meant the prompt never showed again.
  const pendingShowRef = useRef(false);

  const showInstallPrompt = useCallback(() => {
    // Defer while a FAB is present (#3096): do NOT consume a show from the
    // 3-show quota and do NOT make the prompt visible — the render guards would
    // suppress it anyway, so incrementing here would silently burn all shows for
    // a user who only ever opens FAB views. Record the intent so the fabCount
    // effect can replay it once the last FAB is gone. Sticky-visible semantics
    // for an already-shown prompt are unaffected (this only gates new shows).
    if (fabCountRef.current > 0) {
      pendingShowRef.current = true;
      return;
    }
    // Actually showing now — clear any pending intent so a later FAB-unmount
    // replay can't double-fire.
    pendingShowRef.current = false;
    if (!isEligibleToShow()) return;
    incrementShowCount();
    setIsPromptVisible(true);
  }, []);

  const registerFab = useCallback(() => {
    fabCountRef.current += 1;
    setFabCount((c) => c + 1);
    return () => {
      fabCountRef.current = Math.max(0, fabCountRef.current - 1);
      setFabCount((c) => Math.max(0, c - 1));
    };
  }, []);

  // Replay a deferred show once the FAB count truly SETTLES at 0 (#3096).
  // This must NOT live in registerFab's unregister callback: during an A->B
  // FAB swap (navigating between two FAB-bearing views) the outgoing FAB's
  // unregister drives fabCountRef to a transient 0 BEFORE the incoming FAB
  // registers, which would fire the replay over the incoming FAB and burn a
  // quota slot. React batches the A-unregister + B-register setFabCount calls
  // into one re-render, so this effect observes the SETTLED fabCount (still > 0
  // across a swap) and runs only when the count is genuinely 0 — a real
  // FAB-free view. showInstallPrompt re-checks eligibility and consumes exactly
  // one quota slot only if it actually shows; pendingShowRef guards single-fire.
  useEffect(() => {
    if (fabCount === 0 && pendingShowRef.current) {
      pendingShowRef.current = false;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- #3601 Intentional: replay the deferred show when the FAB count settles at 0. Single-fire (pendingShowRef cleared first), so no cascading loop.
      showInstallPrompt();
    }
  }, [fabCount, showInstallPrompt]);

  const hideInstallPrompt = useCallback(() => {
    setIsPromptVisible(false);
  }, []);

  /** Permanently dismiss — never show again on this device */
  const dismissInstallPrompt = useCallback(() => {
    try {
      localStorage.setItem(DISMISSED_KEY, 'true');
    } catch {
      // Ignore storage errors
    }
    setIsPromptVisible(false);
  }, []);

  const value = {
    showInstallPrompt,
    hideInstallPrompt,
    dismissInstallPrompt,
    isPromptVisible,
    isFabPresent: fabCount > 0,
    registerFab,
  };

  return (
    <PWAContext.Provider value={value}>
      {children}
    </PWAContext.Provider>
  );
}
